
import Auth from '@/components/page-specific/auth';
import AuthLoginForm from '@/components/page-specific/auth/forms/login';
import Gutter from '@/ui-kit/components/gutter';
import Typography from '@/ui-kit/components/typography';
import Link from '@/ui-kit/components/link';
import { authLoginFormGroup } from '@/components/page-specific/auth/forms/login/controls';
import { signin, resendSignUp } from '@/utils/auth';
import { track } from '@/utils/analytics';
import { getAction, INIT } from '@/store/user/actions';
import { StyleColorSchemeType, StyleSizeType, StylePositionType } from '@/ui-kit/styles/enums';
import { TypographyType } from '@/ui-kit/components/typography/enums';
import routes from '@/constants/routes';
import redirect from '@/utils/auth/redirect';
import headMeta from '@/constants/headMeta';
import { FooterLink, FooterLinkProp } from '@/components/page-specific/auth/footer-links/enums';
import { FooterLinkUrl } from '@/components/page-specific/auth/footer-links/constants';
import { SubmitButtonTitle, AuthSlotName } from '@/components/page-specific/auth/enums';
import { AUTH_FORM_LEGENDS, ERROR_EXCEPTIONS, AUTH_FORM_TITLES } from '@/components/page-specific/auth/constants';
import { AuthLoginTrackEvent } from '@/constants/AnalyticEvents';
import { Layout } from '@/enums';

const DEFAULT_DESTINATION = '/';

export default redirect(
  {
    name: 'LoginPage',
    components: {
      Auth,
      AuthLoginForm,
      Gutter,
      Typography,
      Link,
    },
    head: {
      title: 'Log in to access your Bode account | Bode Canada',
      meta: [
        { hid: 'description', name: 'description', content: 'Log in to your account to buy and sell your home with Bode ' },
        { property: 'og:title', content: 'Log in to access your Bode account | Bode Canada' },
        { property: 'og:description', content: 'Log in to your account to buy and sell your home with Bode.' },
        { property: 'og:url', content: `${headMeta.domain}${routes.login}` },
        { property: 'og:site_name', content: headMeta.siteName },
        { property: 'og:type', content: headMeta.type },
      ],
    },
    layout: Layout.Basic,
    data() {
      return {
        StyleColorSchemeType,
        StyleSizeType,
        StylePositionType,
        TypographyType,
        SubmitButtonTitle,
        AuthSlotName,
        routes,
        ERROR_EXCEPTIONS,
        email: this.$route.params.email,
        password: '',
        isShowErrorMessage: false,
        errorMessage: null,
        isLoading: false,
      };
    },
    async created() {
      const {
        params: { email, password },
        query: { referrer },
      } = this.$route;

      if (email && password) {
        this.isLoading = true;

        await this.login(email, password, async (e, user) => {
          if (!e) {
            const goTo = referrer || DEFAULT_DESTINATION;

            if (user.firstTimeLogin && referrer !== routes.proMarketplacePortal) {
              this.$router.push(`${routes.signupAdditionalInformation}?referrer=${encodeURIComponent(goTo)}`);
            } else {
              this.$router.push(goTo);
            }
          }

          this.isLoading = false;
        });
      }
    },
    computed: {
      title() {
        return AUTH_FORM_TITLES.Login;
      },
      legend() {
        return AUTH_FORM_LEGENDS.Login;
      },
      referrer() {
        return this.$route.query.referrer
          ? decodeURIComponent(this.$route.query.referrer)
          : DEFAULT_DESTINATION;
      },
      queries() {
        return Object.entries(this.$route.query)
          .reduce((query, [key, value]) => query.concat(`${key}=${value}&`), '?');
      },
      resetPasswordRedirect() {
        return `${routes.resetPassword}${this.queries}`;
      },
      signUpRedirect() {
        return `${routes.signup}${this.queries}`;
      },
      labelColorScheme() {
        return StyleColorSchemeType.Secondary;
      },
      isFormValid() {
        return authLoginFormGroup.isValid;
      },
      formValue() {
        return authLoginFormGroup.value;
      },
      footerLinks() {
        return [
          {
            [FooterLinkProp.Title]: FooterLink.PrivacyPolicy,
            [FooterLinkProp.Url]: FooterLinkUrl[FooterLink.PrivacyPolicy],
          },
          {
            [FooterLinkProp.Title]: FooterLink.TermsPolicy,
            [FooterLinkProp.Url]: FooterLinkUrl[FooterLink.TermsPolicy],
          },
        ];
      },
    },
    methods: {
      validateForm() {
        authLoginFormGroup.validate();
      },
      async submit() {
        this.validateForm();

        if (!this.isFormValid) {
          return;
        }

        const { email, password } = this.formValue;
        this.isLoading = true;

        await this.login(email, password, async (e, user) => {
          if (e) {
            this.isLoading = false;

            // eslint-disable-next-line prefer-const
            let { name, __type } = e;

            if (name === ERROR_EXCEPTIONS.UserNotConfirmedException
              || __type === ERROR_EXCEPTIONS.UserNotConfirmedException) {
              return this.confirm(email, password);
            }

            this.isShowErrorMessage = true;
            this.errorMessage = name;
          }

          if (user?.isAuthenticated && user?.firstTimeLogin) {
            this.$router.replace(routes.signupAdditionalInformation);
          }

          if (user?.isAuthenticated && !e?.message) {
            this.$router.replace(this.referrer);
            postLoginMessage(user);
          }

          return null;
        },
        true);
      },
      async confirm(email, password) {
        track(AuthLoginTrackEvent.NeedToConfirmUser, {
          email,
        });
        const res = await resendSignUp(email);

        this.$router.push({
          name: 'signup-confirm',
          params: {
            email,
            password,
            destination: res.CodeDeliveryDetails.Destination,
          },
        });
      },
      async login(email, password, cb) {
        try {
          track(AuthLoginTrackEvent.SubmitLogInButton, {
            email,
            form: { email, password },
          });
          await signin(email, password);

          const user = await this.$store.dispatch(getAction(INIT), { forceRefresh: true });
          await cb(null, user);

          track(AuthLoginTrackEvent.SuccessLogIn, {
            userId: user.userId,
            method: 'Password',
          });
        } catch (e) {
          track(AuthLoginTrackEvent.LogInError, {
            email,
            // eslint-disable-next-line no-underscore-dangle
            error: e.name || e.__type,
          });
          cb(e);
        }
      },
    },
  },
  (user, referer) => {
    postLoginMessage(user);
    return referer || DEFAULT_DESTINATION;
  },
);

function postLoginMessage(user) {
  const { opener } = window;
  if (opener) {
    opener.postMessage(
      {
        topic: 'login',
        user,
      },
      window.location.origin,
    );
  }
}
